import React, { useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import useStore from '../../store/useStore';

const CategorySidebar = ({
  priceRange,
  setPriceRange,
  discountFilter = 0,
  setDiscountFilter,
  mobileMode = false,
  onClose,
}) => {
  const [searchParams] = useSearchParams();
  const { categoryId } = useParams();
  const {
    categories,
    subcategories,
    selectedCategory,
    setSelectedCategory,
    setSelectedCategoryData
  } = useStore();
  const navigate = useNavigate();

  const rawParam = searchParams.get('category') || categoryId;
  const activeCategorySlug = rawParam || selectedCategory || 'all';

  useEffect(() => {
    if (activeCategorySlug && activeCategorySlug !== 'all') {
      const categoryData = categories.find((cat) => cat.slug === activeCategorySlug);
      if (categoryData) setSelectedCategoryData(categoryData);
    }
  }, [activeCategorySlug, categories, setSelectedCategoryData]);

  const handleSelectCategory = (slug) => {
    setSelectedCategory(slug);
    navigate(slug === 'all' ? '/products' : `/products?category=${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (mobileMode && onClose) onClose();
  };

  return (
    <div className={mobileMode ? 'rounded-2xl border border-[#e6ede6] bg-white p-4 shadow-sm' : 'sticky top-20 rounded-xl bg-white p-4 sm:p-6 shadow-sm max-h-[calc(100vh-6rem)] overflow-y-auto custom-scrollbar pr-3'}>
      <div className={mobileMode ? 'mb-6 border-b border-gray-100 pb-4' : 'mb-8'}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className={`${mobileMode ? 'text-base' : 'text-xl'} font-bold text-gray-900`}>Categories</h3>
          {mobileMode && (
            <button
              onClick={onClose}
              className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600"
            >
              Done
            </button>
          )}
        </div>

        <div className={mobileMode ? 'mobile-scrollbar-none -mx-1 flex gap-2 overflow-x-auto px-1 pb-1' : 'space-y-2'}>
          <button
            onClick={() => handleSelectCategory('all')}
            className={`${mobileMode ? 'shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm' : 'w-full rounded-lg px-4 py-3 text-left'} transition-all ${
              activeCategorySlug === 'all'
                ? 'bg-[#2E7B52] text-white font-semibold'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}
          >
            All Products
          </button>

          {categories.map((category) => (
            <button
              key={category.category_id}
              onClick={() => handleSelectCategory(category.slug)}
              className={`${mobileMode ? 'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm' : 'w-full rounded-lg px-4 py-3 text-left'} transition-all ${
                activeCategorySlug === category.slug
                  ? 'bg-[#2E7B52] text-white font-semibold'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {category.image ? (
                <img
                  src={category.image}
                  alt={category.name}
                  className={`${mobileMode ? 'h-4 w-4' : 'mr-2 inline h-5 w-5'} object-contain`}
                />
              ) : (
                <span className={mobileMode ? '' : 'mr-2'}>Item</span>
              )}
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {subcategories.length > 0 && selectedCategory !== 'all' && (
        <div className={mobileMode ? 'mb-6 border-b border-gray-100 pb-4' : 'mb-8'}>
          <h3 className={`${mobileMode ? 'text-sm' : 'text-lg'} mb-4 font-bold text-gray-900`}>Subcategories</h3>
          <div className={mobileMode ? 'mobile-scrollbar-none -mx-1 flex gap-2 overflow-x-auto px-1 pb-1' : 'space-y-2'}>
            {subcategories.map((subcategory) => (
              <button
                key={subcategory.subcategory_id}
                className={`${mobileMode ? 'shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-xs' : 'w-full rounded-lg px-4 py-2 text-left text-sm'} bg-gray-50 font-medium text-gray-700 transition-all hover:bg-[#2E7B52]/10 hover:text-[#2E7B52]`}
                onClick={() => {
                  // Placeholder for subcategory filtering when implemented.
                }}
              >
                {subcategory.image ? (
                  <img
                    src={subcategory.image}
                    alt={subcategory.name}
                    className={`${mobileMode ? 'mr-2 inline h-3.5 w-3.5' : 'mr-2 inline h-4 w-4'} object-contain`}
                  />
                ) : (
                  <span className="mr-2">Go</span>
                )}
                {subcategory.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={mobileMode ? 'mb-6 border-b border-gray-100 pb-4' : 'mb-8'}>
        <h3 className={`${mobileMode ? 'text-sm' : 'text-xl'} mb-4 font-bold text-gray-900`}>Price Range</h3>
        <div className="space-y-4">
          <div className={`${mobileMode ? 'rounded-xl bg-[#f7faf7] px-3 py-2' : ''} flex items-center justify-between text-sm text-gray-600`}>
            <span>Rs {priceRange[0]}</span>
            <span>Rs {priceRange[1]}</span>
          </div>
          <input
            type="range"
            min="0"
            max="1000"
            value={priceRange[1]}
            onChange={(e) => setPriceRange([0, parseInt(e.target.value, 10)])}
            className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-[#2E7B52]"
          />
          <div className={`flex gap-2 ${mobileMode ? 'flex-row' : 'flex-col sm:flex-row'} flex-wrap`}>
            <button
              onClick={() => setPriceRange([0, 50])}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            >
              Under Rs 50
            </button>
            <button
              onClick={() => setPriceRange([0, 100])}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            >
              Under Rs 100
            </button>
            <button
              onClick={() => setPriceRange([0, 500])}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            >
              Under Rs 500
            </button>
          </div>
        </div>
      </div>

      <div className={mobileMode ? 'mb-6 border-b border-gray-100 pb-4' : 'mb-8'}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`${mobileMode ? 'text-sm' : 'text-xl'} font-bold text-gray-900`}>Discount</h3>
          {discountFilter > 0 && (
            <button
              onClick={() => setDiscountFilter?.(0)}
              className="text-xs font-semibold text-[#2E7B52] hover:underline"
            >
              Reset
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {[10, 20, 30, 50].map((val) => {
            const isSelected = discountFilter === val;
            return (
              <button
                key={val}
                type="button"
                onClick={() => setDiscountFilter?.(isSelected ? 0 : val)}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition-all ${
                  isSelected
                    ? "bg-[#2E7B52] text-white shadow-sm"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {val}%+ OFF
              </button>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default CategorySidebar;
